import { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "../lib/useApi";
import { ApiError } from "../lib/api";
import type { AllocationItem, AllocationResponse, ResourcesResponse } from "../lib/api";
import { useSetTopbarTabs } from "../components/TopbarTabs";

type Tab = "cluster" | "resources";

export function CostAllocationPage() {
  const [tab, setTab] = useState<Tab>("cluster");

  useSetTopbarTabs([
    { id: "cluster", label: "Cluster", active: tab === "cluster", onSelect: () => setTab("cluster") },
    { id: "resources", label: "Resources", active: tab === "resources", onSelect: () => setTab("resources") },
  ]);

  return (
    <>
      <div className="page-head reveal" data-d="1">
        <div className="crumb">Cost · {tab === "cluster" ? "Cluster" : "Resources"}</div>
        <h1>What this workspace costs to run</h1>
        <p>
          Two views: the slice of the AKS cluster this workspace's pods consume (OpenCost reconciles each pod's
          resource requests against the actual billed rate of its node), and the workspace's own Azure resource group
          spend from Cost Management. Together they cover the workspace's footprint.
        </p>
        <div className="callout" style={{ marginTop: 12, background: "var(--bg-panel-2)", border: "1px dashed var(--line-strong)" }}>
          <span className="mk">***</span>
          <span style={{ fontSize: 11 }}>
            May not include all costs. The price of shared regional resources (ACR, the AKS control plane, the cluster's
            vnet, the global DNS zone) is amortized across every workspace and isn't attributed to any one of them here.
          </span>
        </div>
      </div>

      {tab === "cluster" ? <ClusterPanel /> : <ResourcesPanel />}
    </>
  );
}

// ---------- Cluster (OpenCost) ----------

// Bounded by opencost.retention1d in the helm release (currently 365 daily aggregates).
// If you ever shrink that retention, trim this list accordingly to avoid silently
// truncated results at the retention boundary.
type AllocWindowOpt = { id: string; label: string };
const ALLOC_WINDOWS: AllocWindowOpt[] = [
  { id: "1h", label: "1h" },
  { id: "6h", label: "6h" },
  { id: "24h", label: "24h" },
  { id: "7d", label: "7d" },
  { id: "30d", label: "30d" },
  { id: "90d", label: "90d" },
  { id: "month", label: "month-to-date" },
  { id: "lastmonth", label: "last month" },
];

type AllocAggOpt = { id: string; label: string };
const ALLOC_AGGS: AllocAggOpt[] = [
  { id: "controller", label: "Workload" },
  { id: "pod", label: "Pod" },
  { id: "container", label: "Container" },
];

function ClusterPanel() {
  const [window, setWindow] = useState<string>("7d");
  const [aggregate, setAggregate] = useState<string>("controller");
  const [refreshTick, setRefreshTick] = useState(0);

  return (
    <>
      <section className="panel reveal" data-d="2">
        <header>
          <span className="idx">01</span> Query
          <span className="hint">OpenCost · /allocation</span>
        </header>
        <div className="body">
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-end" }}>
            <Selector label="Window" value={window} options={ALLOC_WINDOWS} onChange={setWindow} />
            <Selector label="Group by" value={aggregate} options={ALLOC_AGGS} onChange={setAggregate} />
            <div style={{ flex: 1 }} />
            <button className="btn btn-ghost btn-sm" onClick={() => setRefreshTick((t) => t + 1)}>
              <span className="glyph" /> Refresh
            </button>
          </div>
        </div>
      </section>

      <ClusterResultsPanel
        idx="02"
        title="Workspace · cluster slice"
        hint="workloads in this workspace's namespace"
        window={window}
        aggregate={aggregate}
        scope="self"
        refreshTick={refreshTick}
      />
      <ClusterResultsPanel
        idx="03"
        title="Parent · cluster slice"
        hint="shared infra in the env namespace · ingress-nginx, redis, redpanda"
        window={window}
        aggregate={aggregate}
        scope="parent"
        refreshTick={refreshTick}
      />
    </>
  );
}

function ClusterResultsPanel({
  idx, title, hint, window, aggregate, scope, refreshTick,
}: {
  idx: string;
  title: string;
  hint: string;
  window: string;
  aggregate: string;
  scope: "self" | "parent";
  refreshTick: number;
}) {
  const api = useApi();
  const [data, setData] = useState<AllocationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.getAllocation(window, aggregate, scope));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `${e}`);
      setData(null);
    }
  }, [api, window, aggregate, scope]);

  useEffect(() => { void load(); }, [load, refreshTick]);

  const items = data?.items ?? [];
  const total = useMemo(() => items.reduce((s, x) => s + x.totalCost, 0), [items]);
  const maxRow = useMemo(() => items.reduce((m, x) => Math.max(m, x.totalCost), 0), [items]);

  return (
    <section className="panel reveal" data-d="3">
      <header>
        <span className="idx">{idx}</span> {title}
        <span className="hint">{hint} · {data?.namespace ? <span className="mono">{data.namespace}</span> : null} · total {fmtUsd(total)}</span>
      </header>
      <div className="body">
        {error && <Err msg={error} />}
        {data?.warmingUp && (
          <div className="callout" style={{ marginBottom: 14, background: "var(--bg-panel-2)", border: "1px dashed var(--line-strong)" }}>
            <span className="mk">…</span>
            <span style={{ fontSize: 11 }}>{data.message ?? "Collector is initializing — first samples take a few minutes."}</span>
          </div>
        )}
        {data === null && !error ? (
          <Loading />
        ) : items.length === 0 && !data?.warmingUp ? (
          <span className="faint">no allocation data for this window</span>
        ) : items.length > 0 ? (
          <div style={{ border: "1px solid var(--line)" }}>
            <table className="grid">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>CPU</th>
                  <th>RAM</th>
                  <th>PV</th>
                  <th>Network</th>
                  <th>LB</th>
                  <th>Total</th>
                  <th style={{ width: "30%" }}>Share</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: "var(--bg-panel-2)" }}>
                  <td className="mono"><b>Total</b></td>
                  <td className="mono">{fmtUsd(sum(items, x => x.cpuCost))}</td>
                  <td className="mono">{fmtUsd(sum(items, x => x.ramCost))}</td>
                  <td className="mono">{fmtUsd(sum(items, x => x.pvCost))}</td>
                  <td className="mono">{fmtUsd(sum(items, x => x.networkCost))}</td>
                  <td className="mono">{fmtUsd(sum(items, x => x.loadBalancerCost))}</td>
                  <td className="mono" style={{ color: "var(--cyan)", fontWeight: 700 }}>{fmtUsd(total)}</td>
                  <td />
                </tr>
                {items.map((row) => (
                  <tr key={row.name}>
                    <td className="mono"><b>{row.name}</b></td>
                    <td className="mono">{fmtUsd(row.cpuCost)}</td>
                    <td className="mono">{fmtUsd(row.ramCost)}</td>
                    <td className="mono">{fmtUsd(row.pvCost)}</td>
                    <td className="mono">{fmtUsd(row.networkCost)}</td>
                    <td className="mono">{fmtUsd(row.loadBalancerCost)}</td>
                    <td className="mono" style={{ color: "var(--cyan)", fontWeight: 600 }}>{fmtUsd(row.totalCost)}</td>
                    <td><Bar value={row.totalCost} max={maxRow} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </section>
  );
}

// ---------- Resources (Azure Cost Management) ----------

type TimeframeOpt = { id: string; label: string };
const TIMEFRAMES: TimeframeOpt[] = [
  { id: "WeekToDate", label: "week-to-date" },
  { id: "MonthToDate", label: "month-to-date" },
  { id: "BillingMonthToDate", label: "billing month-to-date" },
  { id: "TheLastWeek", label: "last week" },
  { id: "TheLastMonth", label: "last month" },
  { id: "TheLastBillingMonth", label: "last billing month" },
];

type DimensionOpt = { id: string; label: string };
const DIMENSIONS: DimensionOpt[] = [
  { id: "ServiceName", label: "Service" },
  { id: "ResourceId", label: "Resource" },
  { id: "ResourceType", label: "Resource type" },
  { id: "MeterCategory", label: "Meter category" },
];

function ResourcesPanel() {
  const api = useApi();
  const [timeframe, setTimeframe] = useState<string>("MonthToDate");
  const [dimension, setDimension] = useState<string>("ServiceName");
  const [data, setData] = useState<ResourcesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Cost Management throttles aggressively. On a 429 we hold a cooldown (Azure's advised wait),
  // disable Refresh, count down, then auto-retry once — rather than letting the user re-slam it.
  const [cooldown, setCooldown] = useState(0);
  const [throttled, setThrottled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getResourceCost(timeframe, dimension));
      setThrottled(false);
      setCooldown(0);
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        setThrottled(true);
        setCooldown(Math.max(5, e.retryAfterSeconds ?? 30));
      } else {
        setError(e instanceof ApiError ? e.message : `${e}`);
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api, timeframe, dimension]);

  useEffect(() => { void load(); }, [load]);

  // Tick the throttle cooldown down to zero…
  useEffect(() => {
    if (!throttled || cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [throttled, cooldown]);

  // …then auto-retry once it elapses.
  useEffect(() => {
    if (throttled && cooldown === 0) {
      setThrottled(false);
      void load();
    }
  }, [throttled, cooldown, load]);

  const maxRow = useMemo(() => data?.rows.reduce((m, x) => Math.max(m, x.cost), 0) ?? 0, [data]);

  return (
    <>
      <section className="panel reveal" data-d="2">
        <header>
          <span className="idx">01</span> Query
          <span className="hint">Azure Cost Management · {data?.resourceGroup ?? "workspace RG"}</span>
        </header>
        <div className="body">
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-end" }}>
            <Selector label="Timeframe" value={timeframe} options={TIMEFRAMES} onChange={setTimeframe} />
            <Selector label="Group by" value={dimension} options={DIMENSIONS} onChange={setDimension} />
            <div style={{ flex: 1 }} />
            <button
              className={"btn btn-ghost btn-sm" + (loading ? " loading" : "")}
              disabled={loading || throttled}
              onClick={() => void load()}
            >
              <span className="glyph" /> Refresh
            </button>
          </div>
        </div>
      </section>

      <section className="panel reveal" data-d="3">
        <header>
          <span className="idx">02</span> Spend · {timeframe} · by {dimension}
          <span className="hint">total {fmtCurrency(data?.total ?? 0, data?.currency)}</span>
        </header>
        <div className="body">
          {error && <Err msg={error} />}
          {throttled && (
            <div className="callout" style={{ marginBottom: 14, background: "var(--bg-panel-2)", border: "1px dashed var(--line-strong)" }}>
              <span className="mk">429</span>
              <span style={{ fontSize: 11 }}>
                Azure Cost Management is rate-limiting requests — retrying in {cooldown}s. (Results are cached for
                ~15 min, so this is usually a brief, shared throttle.)
              </span>
            </div>
          )}
          {data === null && !error && !throttled ? (
            <Loading />
          ) : data && data.rows.length === 0 ? (
            <span className="faint">no spend in this timeframe (newly provisioned, or the data hasn't landed yet — Azure billing data has up to 24h lag)</span>
          ) : data ? (
            <div style={{ border: "1px solid var(--line)" }}>
              <table className="grid">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Cost</th>
                    <th style={{ width: "55%" }}>Share</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ background: "var(--bg-panel-2)" }}>
                    <td className="mono"><b>Total</b></td>
                    <td className="mono" style={{ color: "var(--cyan)", fontWeight: 700 }}>{fmtCurrency(data.total, data.currency)}</td>
                    <td />
                  </tr>
                  {data.rows.map((row) => (
                    <tr key={row.name}>
                      <td className="mono" style={{ wordBreak: "break-all" }}>{shortResourceName(row.name, dimension)}</td>
                      <td className="mono" style={{ color: "var(--cyan)", fontWeight: 600 }}>{fmtCurrency(row.cost, row.currency)}</td>
                      <td><Bar value={row.cost} max={maxRow} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </section>
    </>
  );
}

// ---------- shared bits ----------

function Selector({ label, value, options, onChange }: {
  label: string;
  value: string;
  options: { id: string; label: string }[];
  onChange: (id: string) => void;
}) {
  return (
    <div>
      <div className="faint mono" style={{ fontSize: 10, letterSpacing: "0.18em", marginBottom: 6 }}>{label.toUpperCase()}</div>
      <div style={{ display: "flex", gap: 0, border: "1px solid var(--line)" }}>
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            className={"btn btn-sm " + (o.id === value ? "btn-primary" : "btn-ghost")}
            style={{ borderRadius: 0, borderRight: "1px solid var(--line)" }}
            onClick={() => onChange(o.id)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Bar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div style={{ background: "var(--bg-inset)", border: "1px solid var(--line)", height: 14, position: "relative" }}>
      <div style={{ background: "var(--cyan-dim)", width: `${pct}%`, height: "100%" }} />
    </div>
  );
}

function Loading() {
  return <div className="faint mono" style={{ fontSize: 11, letterSpacing: "0.16em" }}>loading…</div>;
}

function Err({ msg }: { msg: string }) {
  return (
    <div className="callout error" style={{ marginBottom: 14 }}>
      <span className="mk">err</span>
      <span>{msg}</span>
    </div>
  );
}

function sum(rows: AllocationItem[], pick: (r: AllocationItem) => number): number {
  return rows.reduce((s, r) => s + pick(r), 0);
}

function fmtUsd(v: number): string {
  if (!isFinite(v)) return "—";
  if (v >= 100) return `$${v.toFixed(0)}`;
  if (v >= 1) return `$${v.toFixed(2)}`;
  return `$${v.toFixed(3)}`;
}

function fmtCurrency(v: number, currency: string | null | undefined): string {
  if (!isFinite(v)) return "—";
  // Most installs are USD/AUD; show 3-letter code suffix for clarity if present.
  const formatted = v >= 100 ? v.toFixed(0) : v >= 1 ? v.toFixed(2) : v.toFixed(3);
  return currency ? `${formatted} ${currency}` : `$${formatted}`;
}

function shortResourceName(name: string, dimension: string): string {
  // ResourceId rows arrive as full ARM ids — strip the prefix so the table stays scannable.
  if (dimension === "ResourceId" && name.startsWith("/subscriptions/")) {
    const parts = name.split("/");
    return parts.slice(-2).join("/");
  }
  return name;
}
